import { StartopdrachtType } from './startopdrachten.service';

export type InstructionReference = string;

export type StartopdrachtCoachMark = {
  readonly highlightSelector: string;
  readonly desktopHighlightSelector: string;
  readonly titel: string;
  readonly beschrijving: string;
  readonly meerInfoLabel: string;
  readonly instructionReference: InstructionReference;
};

const profielTabButton = {
  highlightSelector: '#tab-button-profiel',
  desktopHighlightSelector: '#sidebar-tab-button-profiel',
};

const mijnTeamTabButton = {
  desktopHighlightSelector: '#sidebar-tab-mijn-profiel',
};

const doelTabButton = {
  highlightSelector: '#tab-button-doelen',
  desktopHighlightSelector: '#sidebar-tab-button-doelen',
};

const verhaalTabButton = {
  highlightSelector: '#tab-button-verhaal',
  desktopHighlightSelector: '#sidebar-tab-button-verhaal',
};

const berichtenTabButton = {
  highlightSelector: '#tab-button-berichten',
  desktopHighlightSelector: '#sidebar-tab-button-berichten',
};

export const startopdrachtCoachMarks: Record<StartopdrachtType, StartopdrachtCoachMark> = {
  TEAMLID_GEACTIVEERD: {
    highlightSelector: profielTabButton.highlightSelector,
    desktopHighlightSelector: mijnTeamTabButton.desktopHighlightSelector,
    titel: 'Nodig een teamlid uit',
    beschrijving: `<p>Teamleden kan je uitnodigen in je profiel. Klik op <span class="!font-medium">“Mijn team”</span> en nodig je vrienden, familie of begeleiders uit.</p`,
    meerInfoLabel: 'Hoe teamleden uitnodigen',
    instructionReference: 'team-wie-uitnodigen',
  },
  UITNODIGING_AANVAARD: {
    ...profielTabButton,
    titel: 'Neem deel aan een team',
    beschrijving: `<p>Deelnemen aan een team kan je in je profiel. Klik op <span class="!font-medium">“Teams”</span> en aanvaard een uitnodiging.</p>`,
    meerInfoLabel: 'Hoe deelnemen aan een team',
    instructionReference: 'team-deelnemen',
  },
  ARTIKEL_BEWAARD: {
    ...profielTabButton,
    titel: 'Bewaar een artikel',
    beschrijving: `<p>Artikels kan je bewaren in je profiel. Klik op <span class="!font-medium">“Bewaard”</span> en bewaar een artikel.</p>`,
    meerInfoLabel: 'Hoe artikels bewaren',
    instructionReference: 'ondersteuning-artikel-bewaren',
  },
  HULPLIJN_BEWAARD: {
    ...profielTabButton,
    titel: 'Bewaar een hulplijn',
    beschrijving: `<p>Hulplijnen kan je bewaren in je profiel. Klik op <span class="!font-medium">“Bewaard”</span> en bewaar een hulplijn.</p`,
    meerInfoLabel: 'Hoe hulplijnen bewaren',
    instructionReference: 'ondersteuning-hulplijn-bewaren',
  },
  DOEL_VOORGESTELD: {
    ...doelTabButton,
    titel: 'Stel een doel voor',
    beschrijving: `<p>In de doelen van een jongere kan je een doel voorstellen. Klik op <span class="!font-medium">“Doel voorstellen”</span> en verzend het doel.</p>`,
    meerInfoLabel: 'Hoe doelen voorstellen',
    instructionReference: 'doelen-doel-voorstellen',
  },
  DOEL_TOEGEVOEGD: {
    ...doelTabButton,
    titel: 'Voeg je eerste doel toe',
    beschrijving: `<p>In je doelen kan je een doel toevoegen. Klik op <span class="!font-medium">“Doel toevoegen”</span> en werk aan je doel.</p>`,
    meerInfoLabel: 'Hoe doelen toevoegen',
    instructionReference: 'doelen-doel-toevoegen',
  },
  GEBEURTENIS_VOORGESTELD: {
    ...verhaalTabButton,
    titel: 'Stel een gebeurtenis voor',
    beschrijving: `<p>In het verhaal van een jongere kan je een gebeurtenis voorstellen. Klik op <span class="!font-medium">“Gebeurtenis voorstellen”</span> en verzend de gebeurtenis.</p>`,
    meerInfoLabel: 'Hoe gebeurtenissen voorstellen',
    instructionReference: 'verhaal-gebeurtenis-voorstellen',
  },
  GEBEURTENIS_TOEGEVOEGD: {
    ...verhaalTabButton,
    titel: 'Voeg je eerste gebeurtenis toe',
    beschrijving: `<p>In je verhaal kan je een gebeurtenis toevoegen. Klik op <span class="!font-medium">“Gebeurtenis toevoegen”</span> en deel je verhaal.</p>`,
    meerInfoLabel: 'Hoe gebeurtenissen toevoegen',
    instructionReference: 'verhaal-gebeurtenis-toevoegen',
  },
  EMOJI_REACTIE_DOEL_TOEGEVOEGD: {
    ...doelTabButton,
    titel: 'Reageer op een doel',
    beschrijving: `<p>In de doelen van een jongere kan je reageren op een doel. Kies een doel en geef een <span class="!font-medium">"emoji-reactie"</span>.</p>`,
    meerInfoLabel: 'Hoe reageren op doelen',
    instructionReference: 'doelen-emoji-reactie-toevoegen',
  },
  AFSPRAAK_TOEGEVOEGD: {
    ...profielTabButton,
    titel: 'Voeg een afspraak toe',
    beschrijving: `<p>Afspraken kan je bewaren in je profiel. Klik op <span class="!font-medium">“Kalender”</span> en voeg een afspraak toe.</p>`,
    meerInfoLabel: 'Hoe afspraken toevoegen',
    instructionReference: 'profiel-afspraak-toevoegen',
  },
  BEELDBELAFSPRAAK_GEPLAND: {
    ...profielTabButton,
    titel: 'Plan een videogesprek',
    beschrijving: `<p>Videogesprekken kan je plannen in je profiel. Klik op <span class="!font-medium">“Kalender”</span> en plan een videogesprek.</p>`,
    meerInfoLabel: 'Hoe videogesprekken plannen',
    instructionReference: 'profiel-beeldbel-afspraak-plannen',
  },
  DOEL_STAP_AFGEVINKT: {
    ...doelTabButton,
    titel: 'Zet je eerste stap bij een doel',
    beschrijving: `<p>In je doelen kan je stappen toevoegen en uitvoeren. Klik op <span class="!font-medium">een doel</span> en vink een stap af.</p>`,
    meerInfoLabel: 'Hoe werken aan doelen',
    instructionReference: 'doelen-stap-afvinken',
  },
  NOTITIE_BEWAARD: {
    ...profielTabButton,
    titel: 'Voeg een notitie toe',
    beschrijving: `<p>Notities kan je bewaren in je profiel. Klik op <span class="!font-medium">“Bewaard”</span> en voeg een notitie toe.</p>`,
    meerInfoLabel: 'Hoe notities toevoegen',
    instructionReference: 'profiel-notitie-toevoegen',
  },
  GESPREK_BERICHT_VERSTUURD: {
    ...berichtenTabButton,
    titel: 'Start een gesprek',
    beschrijving: `<p>Klik op <span class="!font-medium">"Berichten"</span> en kies een gesprek om berichten te sturen.</p>`,
    meerInfoLabel: 'Hoe berichten sturen',
    instructionReference: 'berichten-bericht-sturen',
  },
  DOEL_BERICHT_VERSTUURD: {
    ...doelTabButton,
    titel: 'Stuur een bericht bij een doel',
    beschrijving: `<p>Kies een doel en klik op <span class="!font-medium">"Gesprek openen"</span> om samen te werken.</p>`,
    meerInfoLabel: 'Hoe berichten sturen bij een doel',
    instructionReference: 'doelen-bericht-sturen',
  },
  GESPREK_ARTIKEL_GEDEELD: {
    ...profielTabButton,
    titel: 'Deel een artikel',
    beschrijving: `<p>Interessant artikel aan het lezen? Klik op <span class="!font-medium">"Delen"</span> en stuur het door.</p>`,
    meerInfoLabel: 'Hoe artikels delen',
    instructionReference: 'ondersteuning-artikel-delen',
  },
  GESPREK_HULPLIJN_GEDEELD: {
    ...profielTabButton,
    titel: 'Deel een hulplijn',
    beschrijving: `<p>Interessante hulplijn aan het bekijken? Klik op <span class="!font-medium">"Delen"</span> en stuur het door.</p>`,
    meerInfoLabel: 'Hoe hulplijnen delen',
    instructionReference: 'ondersteuning-hulplijn-delen',
  },
  BERICHT_EMOJI_VERSTUURD: {
    ...berichtenTabButton,
    titel: 'Reageer op een bericht',
    beschrijving: `<p>Selecteer een bericht en klik op een <span class="!font-medium">emoji</span> om je reactie te sturen.</p>`,
    meerInfoLabel: `Hoe emoji's sturen`,
    instructionReference: 'berichten-reactie-sturen',
  },
};
